import { useMemo } from 'react';
import { dueAging } from '../../../lib/taskStatus';
import { clientOf, assigneeOf, initialsOf } from '../../../lib/taskViews';

// The timeline: every task placed on one shared date axis.
//
// ⚠️ A TASK HERE HAS A DUE DATE AND NOTHING ELSE — no start, no duration. So
// this is NOT a Gantt chart and must not pretend to be one: a bar drawn from
// "created" to "due" would show a span nobody planned and imply work is
// underway across it. Each task is a MARKER at its deadline, and the only thing
// the horizontal axis encodes is when that falls.
//
// ⚠️ THE WINDOW IS DERIVED FROM THE TASKS, THEN CLAMPED. A single task dated
// 2031 would otherwise squeeze the other sixty into three pixels. Undated tasks
// are listed with no marker rather than dropped — "this has no deadline" is
// exactly what a deadline view should surface, and silently hiding them is how
// work goes missing.

const DAY = 86400000;
const MIN_DAYS = 21;   // A fortnight is too tight to read a month-end rush on.
const MAX_DAYS = 120;  // Past a quarter the ticks stop being legible.

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

function TaskTimelineView({ groups, onOpen }) {
  const all = useMemo(() => groups.flatMap((g) => g.tasks), [groups]);

  const { from, span, ticks, todayPct } = useMemo(() => {
    const today = startOfDay(new Date());
    const dates = all
      .map((t) => t.dueDate && startOfDay(t.dueDate))
      .filter((d) => d && !Number.isNaN(d.getTime()));

    // Today is always in frame: a timeline whose window excludes now cannot
    // show what is late, which is the only question being asked of it.
    const lo = new Date(Math.min(today.getTime(), ...dates.map((d) => d.getTime())) || today.getTime());
    const hi = new Date(Math.max(today.getTime(), ...dates.map((d) => d.getTime())) || today.getTime());

    const start = new Date(lo.getTime() - 2 * DAY);
    let days = Math.ceil((hi - start) / DAY) + 2;
    days = Math.max(MIN_DAYS, Math.min(MAX_DAYS, days));

    // Weekly ticks, labelled by the date they fall on.
    const marks = [];
    for (let i = 0; i <= days; i += 7) {
      marks.push({ pct: (i / days) * 100, at: new Date(start.getTime() + i * DAY) });
    }

    return {
      from: start,
      span: days,
      ticks: marks,
      todayPct: ((today - start) / DAY / days) * 100,
    };
  }, [all]);

  const pctFor = (due) => {
    const d = startOfDay(due);
    const p = ((d - from) / DAY / span) * 100;
    // Clamped so a task just outside the clamped window still renders at the
    // edge rather than being positioned off the track and disappearing.
    return Math.max(0, Math.min(98, p));
  };

  return (
    <div className="tv-timeline">
      <div className="tv-tl-axis">
        <div className="tv-tl-gutter" />
        <div className="tv-tl-track tv-tl-track-head">
          {ticks.map((t) => (
            <span key={t.at.toISOString()} className="tv-tl-tick" style={{ left: `${t.pct}%` }}>
              {fmtDate(t.at)}
            </span>
          ))}
        </div>
      </div>

      {groups.map((g) => (
        <section className="tv-tl-group" key={g.key}>
          <header className="tv-group-head">
            <span className="tv-group-name">{g.label}</span>
            <span className="tv-group-count">{g.tasks.length}</span>
          </header>

          {g.tasks.map((t) => {
            const age = dueAging(t);
            const who = assigneeOf(t);
            const client = clientOf(t);
            return (
              <div className="tv-tl-row" key={t._id}>
                <div className="tv-tl-gutter">
                  <span className={`tv-avatar${who ? '' : ' tv-avatar-none'}`} aria-hidden="true">
                    {who ? initialsOf(who.name) : '–'}
                  </span>
                  <button type="button" className="tv-cell-link tv-tl-name" onClick={() => onOpen(t)}>
                    {t.title}
                  </button>
                </div>

                <div className="tv-tl-track">
                  <span className="tv-tl-today" style={{ left: `${todayPct}%` }} aria-hidden="true" />
                  {t.dueDate ? (
                    <button
                      type="button"
                      className={`tv-tl-mark tv-mark-${age.tone}`}
                      style={{ left: `${pctFor(t.dueDate)}%` }}
                      onClick={() => onOpen(t)}
                      title={`${t.title} — ${fmtDate(t.dueDate)}`}
                    >
                      <span className="tv-tl-bar" aria-hidden="true" />
                      <span className="tv-tl-label">
                        {client?.displayName || 'Internal'} · {fmtDate(t.dueDate)}
                        {age.label ? ` · ${age.label}` : ''}
                      </span>
                    </button>
                  ) : (
                    <span className="tv-tl-undated">No due date</span>
                  )}
                </div>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

export default TaskTimelineView;
